import { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import GuruLayout from "../../components/layout/GuruLayout";
import { usePopup } from "../../components/modal/Popup"; // ← dari folder modal

const API = "https://backend.smpn1kamal.sch.id/api";
const STATUS_OPTIONS = ["hadir", "izin", "sakit", "alfa"];

const STATUS_STYLE = {
  hadir: { bg: "bg-emerald-500", light: "bg-emerald-50 text-emerald-700 border-emerald-200", dot: "bg-emerald-500", label: "Hadir" },
  izin:  { bg: "bg-amber-400",   light: "bg-amber-50 text-amber-700 border-amber-200",       dot: "bg-amber-400",   label: "Izin"  },
  sakit: { bg: "bg-blue-500",    light: "bg-blue-50 text-blue-700 border-blue-200",          dot: "bg-blue-500",    label: "Sakit" },
  alfa:  { bg: "bg-red-500",     light: "bg-red-50 text-red-700 border-red-200",             dot: "bg-red-500",     label: "Alfa"  },
};

export default function PresensiManual() {
  const navigate = useNavigate();
  const { success: popSuccess, error: popError, PopupPortal } = usePopup();

  const [jadwal, setJadwal]                   = useState(null);
  const [siswaList, setSiswaList]             = useState([]);
  const [selectedSiswaId, setSelectedSiswaId] = useState("");
  const [loading, setLoading]                 = useState(true);
  const [submitting, setSubmitting]           = useState(false);
  const [closing, setClosing]                 = useState(false);

  useEffect(() => {
    const token = sessionStorage.getItem("token");
    const init = async () => {
      try {
        const res = await axios.get(`${API}/jadwal/aktif`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (!res.data.active) {
          toast.error("Tidak ada jadwal aktif saat ini");
          navigate("/guru/presensi");
          return;
        }

        const jadwalAktif = res.data.jadwal;
        setJadwal(jadwalAktif);

        const siswaRes = await axios.get(
          `${API}/datasiswa/kelas/${jadwalAktif.id_kelas}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        let statusMap = {};
        try {
          const presensiRes = await axios.get(
            `${API}/presensi/by-kelas?id_kelas=${jadwalAktif.id_kelas}&id_mapel=${jadwalAktif.id_mapel}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          for (const p of presensiRes.data.data ?? []) {
            statusMap[p.id] = p.status;
          }
        } catch { /* fallback */ }

        const dataSiswa = siswaRes.data.map((s) => ({
          ...s,
          status: statusMap[s.id] ?? "alfa",
        }));

        setSiswaList(dataSiswa);
        if (dataSiswa.length > 0) setSelectedSiswaId(dataSiswa[0].id);
        setLoading(false);
      } catch {
        toast.error("Gagal memuat data");
        navigate("/guru/presensi");
      }
    };
    init();
  }, [navigate]);

  const currentSiswa = useMemo(
    () => siswaList.find((s) => s.id === Number(selectedSiswaId)),
    [selectedSiswaId, siswaList]
  );

  const updateStatus = (status) => {
    setSiswaList((prev) =>
      prev.map((s) => (s.id === Number(selectedSiswaId) ? { ...s, status } : s))
    );
  };

  const simpanPresensi = async () => {
    const token = sessionStorage.getItem("token");
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/presensi/manual`,
        { data: siswaList.map((s) => ({ id_siswa: s.id, status: s.status })) },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      popSuccess("Data tersimpan", "Presensi berhasil disimpan");
      return true;
    } catch {
      popError("Gagal menyimpan", "Periksa koneksi internet");
      return false;
    } finally {
      setSubmitting(false);
    }
  };

  const tutupPresensi = async () => {
    setClosing(true);
    const token = sessionStorage.getItem("token");
    try {
      const berhasil = await simpanPresensi();
      if (!berhasil) { setClosing(false); return; }
      await axios.post(
        `${API}/presensi/wa`,
        { id_kelas: jadwal.id_kelas },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      popSuccess("Presensi dikirim", "Notifikasi WA berhasil terkirim");
      setTimeout(() => navigate("/guru/presensi"), 1200);
    } catch {
      popError("Gagal kirim WA", "Periksa koneksi internet");
      setClosing(false);
    }
  };

  const currentIndex = siswaList.findIndex((s) => s.id === Number(selectedSiswaId));

  if (loading) {
    return (
      <GuruLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="flex flex-col items-center gap-3">
            <div className="w-7 h-7 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
            <p className="text-sm text-slate-400">Memuat data...</p>
          </div>
        </div>
      </GuruLayout>
    );
  }

  return (
    <GuruLayout>
      {PopupPortal}

      <div className="max-w-lg mx-auto md:max-w-none px-0 md:px-0 py-0 space-y-4">

        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate("/guru/presensi")}
            className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800 transition-colors flex-shrink-0"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Kembali
          </button>

          <div className="flex-1 min-w-0">
            <p className="text-sm font-bold text-slate-800 truncate">
              {jadwal.nama_kelas}
              <span className="mx-2 text-slate-300">·</span>
              {jadwal.nama_mapel}
            </p>
            <p className="text-xs text-slate-400">
              Presensi Manual — {siswaList.length} siswa
            </p>
          </div>

          <button
            onClick={() => navigate("/guru/presensi/scanner")}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold px-4 py-2 rounded-full transition-all active:scale-95 flex-shrink-0"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 3.5V16M4 4h4v4H4zm12 0h4v4h-4zM4 16h4v4H4z" />
            </svg>
            <span className="hidden sm:inline">Buka Scanner</span>
          </button>
        </div>

        <div className="bg-white border border-slate-100 rounded-2xl p-5 shadow-sm space-y-5">

          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <label className="text-sm font-semibold text-slate-700">Siswa</label>
              <span className="text-xs text-slate-400 bg-slate-50 border border-slate-200 px-2 py-0.5 rounded-full">
                {currentIndex + 1} / {siswaList.length}
              </span>
            </div>
            <select
              value={selectedSiswaId}
              onChange={(e) => setSelectedSiswaId(e.target.value)}
              className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm font-semibold text-slate-800 focus:ring-2 focus:ring-blue-400 focus:border-blue-400 outline-none appearance-none cursor-pointer transition"
            >
              {siswaList.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.nama_lengkap.toUpperCase()}
                </option>
              ))}
            </select>
          </div>

          <div className="border-t border-slate-100" />

          <div className="space-y-2">
            <label className="text-sm font-semibold text-slate-700">Status Kehadiran</label>
            <div className="grid grid-cols-4 gap-2">
              {STATUS_OPTIONS.map((opt) => {
                const st = STATUS_STYLE[opt];
                const active = currentSiswa?.status === opt;
                return (
                  <button
                    key={opt}
                    onClick={() => updateStatus(opt)}
                    className={`py-2.5 rounded-full text-sm font-bold transition-all active:scale-95 border
                      ${active
                        ? `${st.bg} text-white border-transparent`
                        : "bg-white text-slate-500 border-slate-200 hover:bg-slate-50"
                      }`}
                  >
                    {st.label}
                  </button>
                );
              })}
            </div>
          </div>

        </div>

        <div className="grid grid-cols-2 gap-3">
          <button
            onClick={simpanPresensi}
            disabled={submitting || closing}
            className="h-11 rounded-full font-semibold text-sm bg-slate-800 hover:bg-slate-900 text-white transition-all active:scale-95 disabled:opacity-40"
          >
            {submitting ? "Menyimpan..." : "Simpan Sementara"}
          </button>
          <button
            onClick={tutupPresensi}
            disabled={submitting || closing}
            className="h-11 rounded-full font-semibold text-sm bg-red-500 hover:bg-red-600 text-white transition-all active:scale-95 disabled:opacity-40"
          >
            {closing ? "Mengirim..." : "Tutup & Kirim WA"}
          </button>
        </div>

      </div>
    </GuruLayout>
  );
}